import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { config } from '../../../config';
import { useCanStripeCard } from '../../../config/hooks';

import * as providerActs from '../../../entities/providers/actions';
import { geShipmentPrice } from '../../../entities/rates/selectors/shipments.selectors';

import { Layout } from '../../element';
import {
  PayWithCrypto,
  PayWithMaestroCard,
  PayWithMasterCard,
  PayWithPayPal,
  PayWithVisa,
  PayWithApplePay,
  PayWithStripe,
} from '../../payments/payment-method';
import { PiraeusBanking } from '../../payments/banking/piraeus';
import { StripeElements } from '../../payments/payment-method/stripe/elements';

import { PaymentMethods } from '../../../config/providers';
import { PaymentEvents } from '../../../config/analytics';

import { useTranslation } from '../../../i18n';
import { paymentEvent } from '../../../helpers/gtag';
import { scrollToTop } from '../../../helpers/scroll/scrollToTop';

export const RatesPayment: React.FC = () => {
  const [t] = useTranslation('rates');

  useEffect(() => {
    scrollToTop();
  }, []);

  const dispatch = useDispatch();

  const canStripeCard = useCanStripeCard();

  const payment = useSelector(geShipmentPrice);
  const [canStripe, setCanStripe] = useState(true);

  const handlePayPal = useCallback(
    (details, data) => {
      if (!payment) {
        return;
      }

      paymentEvent(PaymentEvents.PayPal);

      // eslint-disable-next-line no-console
      console.log(`Transaction completed by ${details.payer.name.given_name}`);

      dispatch(
        providerActs.payment({
          paymentType: PaymentMethods.PayPal,
          orderID: data.orderID,
          shipment: payment.shipmentID,
          provider: payment.provider,
        })
      );
    },
    [payment]
  );

  const handleCryptoPay = useCallback(
    (details, act) => {
      if (!payment) {
        return;
      }

      if (act.status !== 'succeeded') {
        // eslint-disable-next-line no-console
        console.warn('Failed to pay with Crpyto.com');
        // eslint-disable-next-line no-console
        console.log({
          details,
          act,
        });

        return;
      }

      paymentEvent(PaymentEvents.Crypto);

      dispatch(
        providerActs.payment({
          paymentType: PaymentMethods.Crypto,
          orderID: act.id,
          shipment: payment.shipmentID,
          provider: payment.provider,
        })
      );
    },
    [payment]
  );

  const handleApplePay = useCallback(
    (details: NotTyped) => {
      if (!payment) {
        return;
      }

      paymentEvent(PaymentEvents.ApplePay);

      dispatch(
        providerActs.payment({
          paymentType: PaymentMethods.ApplePay,
          orderID: details.id,
          shipment: payment.shipmentID,
          provider: payment.provider,
        })
      );
    },
    [payment]
  );

  const handleStripeCardPay = useCallback(
    (details: NotTyped) => {
      if (!payment) {
        return;
      }

      paymentEvent(PaymentEvents.StripeCard);

      dispatch(
        providerActs.payment({
          paymentType: PaymentMethods.Stripe,
          orderID: details.id,
          shipment: payment.shipmentID,
          provider: payment.provider,
        })
      );
    },
    [payment]
  );

  const handleCardPay = useCallback(() => {
    if (!payment) {
      return;
    }

    dispatch(
      providerActs.createPiraeusTicket({
        provider: payment.provider,
        shipment: payment.shipmentID,
      })
    );
  }, [payment]);

  if (payment === null) {
    return null;
  }

  const preferredCurrency = payment.localCurrency === 'GBP' ? 'GBP' : 'EUR';

  return (
    <StripeElements>
      <div>
        <h2 className="text-center">{t('rates.payments.title')}</h2>

        <Layout.Row gutter={20}>
          {canStripeCard && (
            <Layout.Col span={24} xs={24} sm={24} md={12} lg={12} className="mb-20">
              <PayWithStripe
                orderID={payment.hash}
                price={payment.priceEUR}
                currency={preferredCurrency}
                onPay={handleStripeCardPay}
              />
            </Layout.Col>
          )}

          {canStripe && (
            <Layout.Col span={24} xs={24} sm={24} md={12} lg={12} className="mb-20">
              <PayWithApplePay
                orderID={payment.hash}
                price={payment.priceEUR}
                currency={preferredCurrency}
                onPay={handleApplePay}
                onNotSupported={() => setCanStripe(false)}
              />
            </Layout.Col>
          )}

          {config.payments.isPayPalEnabled && (
            <Layout.Col span={24} xs={24} sm={24} md={12} lg={12} className="mb-20">
              <PayWithPayPal price={payment.price} currency={payment.localCurrency} onPay={handlePayPal} />
            </Layout.Col>
          )}

          {config.payments.isPirauesEnabled && !canStripeCard && (
            <Layout.Col span={24} xs={24} sm={24} md={12} lg={12} className="mb-20">
              <PayWithVisa price={payment.priceEUR} currency={preferredCurrency} onPay={handleCardPay} />
            </Layout.Col>
          )}

          {config.payments.isPirauesEnabled && !canStripeCard && (
            <>
              <Layout.Col span={24} xs={24} sm={24} md={12} lg={12} className="mb-20">
                <PayWithMasterCard price={payment.priceEUR} currency={preferredCurrency} onPay={handleCardPay} />
              </Layout.Col>
              <Layout.Col span={24} xs={24} sm={24} md={12} lg={12} className="mb-20">
                <PayWithMaestroCard price={payment.priceEUR} currency={preferredCurrency} onPay={handleCardPay} />
              </Layout.Col>
            </>
          )}

          <Layout.Col span={24} xs={24} sm={24} md={12} lg={12} className="mb-20">
            <PayWithCrypto
              orderID={payment.hash}
              price={payment.priceEUR}
              currency={preferredCurrency}
              onPay={handleCryptoPay}
            />
          </Layout.Col>
        </Layout.Row>

        {config.payments.isPirauesEnabled && (
          <PiraeusBanking provider={payment.provider} shipmentID={payment.shipmentID.toString()} />
        )}
      </div>
    </StripeElements>
  );
};
